import DocumentViewModal from "@/components/DocumentViewModal";
import DownloadFile from "@/components/DownloadFile";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import Toaster from "@/components/Toaster";
import { sendUserLoginDetails } from "@/store/slices/internalUsersSlice";
import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/solid";
import { Typography, Button } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { PerformanceDashboard } from "../dashboard";

const InfoField = ({ label, value, children }) => (
    <div className="py-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
        {children}
    </div>
);

const DocField = ({ label, value, docUrl, onView }) => {
    const { t } = useTranslation("internal-user");
    return(
    <div className="py-3">
        <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</p>
            {docUrl && (
                <span className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onView(docUrl)}
                        className="p-1.5 rounded-md hover:bg-gray-200 text-gray-700 hover:text-blue-600 transition"
                        title={t('view_button')}
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <DownloadFile fileUrl={docUrl} className="text-gray-700 hover:text-blue-600" />
                </span>
            )}
        </div>
        <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
    </div>
)};

const Section = ({ title, children }) => (
    <div className="border-b border-gray-200 last:border-b-0 pb-6 last:pb-0">
        <h3 className="text-base font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
            {children}
        </div>
    </div>
);

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return "—";
    }
}

export function ViewInternalUser({ type = "city_representative" }) {
    const { t } = useTranslation("internal-user");
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const page_url = type.replace(" ", "-")?.toLowerCase();
    const [isShowLoader, setIsShowLoader] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [documentUrl, setDocumentUrl] = useState("");

    const user = useSelector((s) => s.internalUser.selected);
    const { loading } = useSelector((s) => s.internalUser);

    useEffect(() => {
        setIsShowLoader(false);
    }, []);

    const openDocument = (url) => {
        setDocumentUrl(url);
        setIsModalOpen(true);
    };

    if (!user && !loading && !isShowLoader) {
        return (
            <div className="mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <ScrollToTop />
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Typography variant="h6" color="gray" className="mb-4">
                        {t('no_user_data_found')}
                    </Typography>
                    <Link
                        to={`/dashboard/${page_url}`}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        {t('back_to_list')}
                    </Link>
                </div>
            </div>
        );
    }

    const handleSendLoginDetails = async (user_id) => {
        try {
            const confirmation = await Swal.fire({
                title: t('send_login_details_prompt_title'),
                text: t('send_login_details_prompt_text'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t('send_login_details_prompt_confirm'),
                cancelButtonText: t('send_login_details_prompt_cancel')
            });

            if (!confirmation.isConfirmed) return;

            const response = await dispatch(sendUserLoginDetails(user_id)).unwrap();

            if (response?.status) {
                Toaster.success(
                    response?.message || t('send_login_details_success')
                );
            } else {
                Toaster.error(
                    response?.message || t('send_login_details_fail')
                );
            }

        } catch (error) {
            Toaster.error(
                error?.message || t('send_login_details_error')
            );
        }
    };

    return (
        <div className="mb-8 flex flex-col gap-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(loading || isShowLoader) && <ShowLoader />}
            <ScrollToTop />

            {/* Header */}
            <div className="flex flex-col gap-4">
                <Link
                    to={`/dashboard/${page_url}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition w-fit"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('back')}
                </Link>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <Typography variant="h5" color="blue-gray" className="font-bold text-gray-900">
                        {t('profile_details_title')}
                    </Typography>
                    <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        onClick={() => navigate(`/dashboard/${page_url}/edit/${id}`)}
                        className="rounded-lg w-fit"
                    >
                        {t('edit_profile_button')}
                    </Button>
                </div>
            </div>

            {/* Profile Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Profile Header */}
            <div className="p-6 md:p-8 bg-gray-50/50 border-b border-gray-100">
                
                {/* Top Row */}
                <div className="flex justify-between items-start">
                    
                    {/* Left Section */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                        <button
                            type="button"
                            onClick={() => user?.profile_image && openDocument(user.profile_image)}
                            className={`flex-shrink-0 rounded-full overflow-hidden shadow-md ${user?.profile_image ? "cursor-pointer ring-2 ring-offset-2 ring-transparent hover:ring-blue-200 transition" : ""}`}
                        >
                            <img
                                src={user?.profile_image || "/img/default-avatar-profile-icon.avif"}
                                alt={user?.first_name || t('profile_alt')}
                                className="w-24 h-24 object-cover"
                            />
                        </button>

                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {user?.first_name} {user?.last_name || ""}
                            </h2>
                            <p className="text-sm font-medium text-gray-600 mt-0.5">
                                {user?.type || "—"} {user?.parent_id == null ? t('manager_badge') : t('member_badge')}
                            </p>
                            <p className="text-xs font-medium text-gray-500 mt-1">
                                ID {String(user?.id ?? "").padStart(4, "0")}
                            </p>
                        </div>
                    </div>

                    {/* Right Button */}
                    <div>
                        <Button
                            type="button"
                            onClick={() => handleSendLoginDetails(user?.id)}
                            className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white "
                        >
                            {t('send_login_details_button')}
                        </Button>
                    </div>

                </div>
            </div>

                {/* Info Sections */}
                <div className="p-6 md:p-8 flex flex-col gap-6">
                    <Section title={t('contact_info_section')}>
                        <InfoField
                            label={t('phone_number_label')}
                            value={[user?.country_code, user?.mobile_number].filter(Boolean).join(" ") || null}
                        />
                        <InfoField
                            label={t('alternate_phone_label')}
                            value={[user?.alternate_country_code, user?.alternate_number].filter(Boolean).join(" ") || null}
                        />
                        <InfoField label={t('email_address_label')} value={user?.email} />
                        <InfoField label={t('member_since_label')} value={formatDate(user?.date)} />
                        <InfoField label={t('gender_label')} value={user?.gender} />
                    </Section>

                    <Section title={t('office_location_section')}>
                        <InfoField label={t('office_label')} value={user?.office_name} />
                        <InfoField label={t('country_label')} value={user?.country_name} />
                        <InfoField label={t('city_label')} value={user?.city_name} />
                    </Section>

                    <Section title={t('documents_section')}>
                        <DocField
                            label={t('id_proof_label')}
                            value={user?.residence_id}
                            docUrl={user?.id_proof}
                            onView={openDocument}
                        />
                    </Section>
                </div>
            </div>
            
            {(user?.roles[0]?.name == 'Sales Representative' || user?.roles[0]?.name == 'City Representative'  || user?.roles[0]?.name == 'Procurement') && 
                <PerformanceDashboard user_id={user?.id} />
            }
            {isModalOpen && (
                <DocumentViewModal documentUrl={documentUrl} setIsDocumentViewModalOpen={setIsModalOpen} />
            )}
        </div>
    );
}

export default ViewInternalUser;
